import { ListItemIcon, Typography, useMediaQuery, Box, Button } from "@material-ui/core";
import { useCallback, useContext, useEffect, useMemo, useState, useTransition } from "react";

import { CalendarSync, MoreHorizontalIcon } from "lucide-react";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import DescriptionIcon from "@material-ui/icons/Description";
import Drawer from "@material-ui/core/Drawer";
import Hidden from "./HiddenSSRCompatible";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import MobileMenu from "./MobileMenu";
import MobileMenuButton from "./MobileMenuButton";
import moment from "moment";
import { observer } from "mobx-react-lite";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import ReceiptIcon from "@material-ui/icons/Receipt";
import SettingsIcon from "@material-ui/icons/Settings";
import { MessageCircle } from "lucide-react";
import { StoreContext } from "../store";
import { useRouter } from "next/router";
import { useStyles } from "../styles/components/Nav.styles";
import useTimeout from "../hooks/useTimeout";
import useTranslation from "next-translate/useTranslation";
import config from "../config";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import { LogoNavIllustration, LogoNav2Illustration } from "./Illustrations";
import {
  FileCheck,
  LayoutDashboard,
  ScrollText,
  Users2,
  BedSingle,
  DoorOpen,
  Wallet,
  Settings,
  CreditCard,
  Globe,
  ListTodo
} from "lucide-react";
import { usePermissions, PermissionGuard } from "../utils/PermissionProvider";
import LanguageSwitcher from "./LanguageSwitcher";

const MENU_ITEMS = [
  {
    key: "dashboard",
    labelId: "Dashboard",
    pathname: "/dashboard",
    Icon: LayoutDashboard,
    dataCy: "dashboardNav",
    permission: "dashboard",
  },
  {
    key: "frontdesk",
    labelId: "Frontdesk",
    pathname: "/frontdesk",
    subPathnames: ["/payment/[tenantId]/[...param]"],
    Icon: ScrollText,
    dataCy: "rentsNav",
    permission: "frontdesk",
  },
  {
    key: "tenants",
    labelId: "Tenants",
    pathname: "/tenants",
    Icon: Users2,
    dataCy: "tenantsNav",
    permission: "tenant",
  },
  {
    key: "rooms",
    labelId: "Rooms",
    pathname: "/rooms",
    Icon: BedSingle,
    dataCy: "propertiesNav",
    permission: "room",
  },
  {
    key: "properties",
    labelId: "Properties",
    pathname: "/properties",
    Icon: DoorOpen,
    dataCy: "propertiesNav",
    permission: "property",
  },
  {
    key: "chatwhatsapp",
    labelId: "Chatwhatsapp",
    pathname: "/chatwhatsapp",
    Icon: MessageCircle,
    dataCy: "propertiesNav",
    permission: "whatsapp",
  },
  {
    key: "website",
    labelId: "Website",
    pathname: "/website",
    subPathnames: ["/website/[propertyId]", "/website/[propertyId]/edit"],
    Icon: Globe,
    dataCy: "websiteNav",
    permission: "website",
  },
  {
    key: "expenses",
    labelId: "Expenses",
    pathname: "/expenses",
    Icon: Wallet,
    dataCy: "expensesNav",
    permission: "expense",
  },
  {
    key: "requests",
    labelId: "Request",
    pathname: "/requests",
    Icon: ListTodo,
    dataCy: "requestsNav",
    permission: "request",
  },
  {
    key: "reports",
    labelId: "Reports",
    pathname: "/reports",
    Icon: FileCheck,
    dataCy: "reportsNav",
    permission: "report",
  },
  {
    key: "settings",
    labelId: "Settings",
    pathname: "/settings",
    Icon: Settings,
    dataCy: "settingsNav",
    permission: "setting",
  },
  {
    key: "subscription",
    labelId: "Subscription",
    pathname: "/subscription",
    Icon: CalendarSync,
    dataCy: "subscriptionNav",
    permission: "subscription.view",
    // style: {
    //   color: "#ffd93d",
    // },
  },
  {
    key: "more",
    labelId: "More",
    pathname: "/",
    Icon: MoreHorizontalIcon,
    dataCy: "moreNav",
  },
];

const MOBILE_MENU_ITEMS = MENU_ITEMS.filter(({ key }) => !["accounting"].includes(key));

function MenuItem({ item, selected, open, onClick, badgeCount = 0 }) {
  const classes = useStyles();
  const { t } = useTranslation("common");

  const onMenuClick = useCallback(
    (event) => {
      onClick(item, event);
    },
    [item, onClick],
  );

  const Icon = useMemo(() => item.Icon, [item.Icon]);

  const buildHref = useCallback(() => {
    let pathname = item.pathname.replace("[yearMonth]", "");
    pathname = pathname.replace("[year]", moment().year());
    return `${config.BASE_PATH}${pathname}`;
  }, [item.pathname]);

  const handleLeftClick = useCallback(
    (event) => {
      // Prevent default anchor behavior for left click
      event.preventDefault();
      onMenuClick(event);
    },
    [onMenuClick],
  );

  return (
    <ListItem
      className={selected ? classes.itemSelected : ""}
      button
      selected={selected}
      component="a"
      href={buildHref()}
      onClick={handleLeftClick}
      target="_blank"
      rel="noopener noreferrer"
      data-cy={item.dataCy}
      style={item.style}
    >
      <ListItemIcon classes={{ root: selected ? classes.itemSelected : classes.item }} style={item.style}>
        <Box position="relative">
          <Icon />
          {badgeCount > 0 && (
            <Box
              component="span"
              aria-label={`${badgeCount} unanswered chats`}
              style={{
                position: "absolute",
                top: -6,
                right: -6,
                backgroundColor: "#bf360c",
                color: "#fff",
                borderRadius: 10,
                minWidth: 16,
                height: 16,
                padding: "0 4px",
                fontSize: 10,
                lineHeight: "16px",
                display: "inline-block",
                textAlign: "center",
                zIndex: 2,
              }}
            >
              {badgeCount}
            </Box>
          )}
        </Box>
      </ListItemIcon>
      <ListItemText
        className={`${classes.itemText} ${open ? classes.itemTextOpen : classes.itemTextClose}`}
        primary={
          <Typography classes={{ root: selected ? classes.itemSelected : classes.item }} noWrap style={item.style}>
            {t(item.labelId)}
          </Typography>
        }
      />
    </ListItem>
  );
}

function Nav() {
  const classes = useStyles();
  const [menuItems, setMenuItems] = useState([]);
  const [, startTransition] = useTransition();
  const [selectedPathname, setSelectedPathname] = useState("");
  const [openDebounced, setOpenDebounced] = useState(false);
  const store = useContext(StoreContext);
  const router = useRouter();
  const { hasPermission, isLoading } = usePermissions();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [showMenu, setShowMenu] = useState(false);
  const unansweredTotal = store.whatsapp?.getTotalUnanswered?.() || 0;
  const { t } = useTranslation("common");

  useEffect(() => {
    const baseMenuItems = isMobile ? MOBILE_MENU_ITEMS : MENU_ITEMS;

    // Filter by permissions and conditions - only if not loading
    const filteredByPermissions = isLoading
      ? [] // Show empty menu while loading
      : baseMenuItems.filter((item) => {
          // Check permission first
          if (item.permission && !hasPermission(item.permission)) {
            return false;
          }

          // Check custom condition
          if (item.condition) {
            return item.condition();
          }

          // For starter plan users, hide "More" menu
          if (store.user.plan === "starter" && item.key === "more") {
            return false;
          }

          return true; // If no permission or condition is specified, include the item
        });

    setMenuItems(filteredByPermissions);

    const menuChanged =
      !store.rbac.menu ||
      store.rbac.menu.length !== filteredByPermissions.length ||
      JSON.stringify(store.rbac.menu.map((i) => i.key)) !== JSON.stringify(filteredByPermissions.map((i) => i.key));

    if (menuChanged) {
      store.rbac?.setMenu(filteredByPermissions);
    }
  }, [isMobile, store.user.isLandlord, store.user.plan, isLoading]);

  const triggerOpen = useTimeout(() => {
    startTransition(() => !openDebounced && setOpenDebounced(true));
  }, 1000);

  const handleMenuClick = useCallback(
    (menuItem, event) => {
      // Handle middle-click or Ctrl/Cmd+Click for opening in new tab
      if (event?.button === 1 || event?.ctrlKey || event?.metaKey) {
        event.preventDefault();
        triggerOpen.clear();

        let pathname = menuItem.pathname.replace("[yearMonth]", store.rent?.periodAsString || "");
        pathname = pathname.replace("[year]", moment().year());

        // Open in new tab
        window.open(`${config.BASE_PATH}${pathname}`, "_blank");
        return;
      }

      // Normal click behavior
      triggerOpen.clear();
      startTransition(() => setSelectedPathname(menuItem.pathname));

      if (store.organization.selected?.name && store.rent?.periodAsString) {
        let pathname = menuItem.pathname.replace("[yearMonth]", store.rent.periodAsString);
        pathname = pathname.replace("[year]", moment().year());
        router.push(`${pathname}`, undefined, {
          locale: store.organization.selected.locale,
        });
      }
    },
    [
      triggerOpen,
      store.organization.selected?.name,
      store.organization.selected?.locale,
      store.rent?.periodAsString,
      router,
    ],
  );

  // Click for more menu
  const handleClickMoreMenu = useCallback(() => {
    setShowMenu((prevShowMenu) => !prevShowMenu);
  }, []);

  const handleCloseButton = useCallback(() => {
    // Close the additional content when the close button is clicked
    setShowMenu(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    !openDebounced && triggerOpen.start();
  }, [openDebounced, triggerOpen]);

  const handleMouseLeave = useCallback(() => {
    triggerOpen.clear();
    startTransition(() => setOpenDebounced(false));
  }, [triggerOpen]);

  return (
    <>
      <Hidden smDown>
        <Drawer
          className={`${openDebounced ? classes.drawerOpen : classes.drawerClose}`}
          variant="permanent"
          classes={{
            paper: openDebounced ? classes.drawerOpen : classes.drawerClose,
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <List className={classes.list}>
            <div className={classes.logoContainer}>
              {openDebounced && <LogoNav2Illustration height={57} className={classes.logoOpen} />}
              {!openDebounced && <LogoNavIllustration height={57} width={57} />}
            </div>
            {menuItems
              .filter((item) => item.key !== "more") // Skip rendering "More" in desktop menu
              .map((item) => (
                <PermissionGuard key={item.key} permission={item.permission}>
                  <MenuItem
                    item={item}
                    selected={
                      selectedPathname.indexOf(item.pathname) !== -1 ||
                      item.subPathnames?.some((p) => selectedPathname.includes(p))
                    }
                    open={openDebounced}
                    onClick={handleMenuClick}
                    badgeCount={item.key === "chatwhatsapp" ? unansweredTotal : 0}
                  />
                </PermissionGuard>
              ))}
            {/* Plan for bottom button */}
            {/* <PermissionGuard permission={"subscription"}>
              <MenuItem
                item={{
                  key: "subscription",
                  labelId: "Subscription",
                  pathname: "/subscription",
                  Icon: CalendarSync,
                  dataCy: 'subscriptionNav',
                  permission: 'subscription',
                }}
                selected={selectedPathname.indexOf("/subscription") !== -1 || []?.some((p) => selectedPathname.includes(p))}
                open={openDebounced}
                onClick={handleMenuClick}
              />
            </PermissionGuard>
            <PermissionGuard permission={"setting"}>
              <MenuItem
                item={{
                  key: "settings",
                  labelId: "Settings",
                  pathname: "/settings",
                  Icon: Settings,
                  dataCy: 'settingsNav',
                  permission: 'setting',
                }}
                selected={selectedPathname.indexOf("/settings") !== -1 || []?.some((p) => selectedPathname.includes(p))}
                open={openDebounced}
                onClick={handleMenuClick}
              />
            </PermissionGuard> */}
          </List>
        </Drawer>
      </Hidden>
      <Hidden mdUp>
        <MobileMenu position="fixed" bottom={0} width="100%" display="flex" justifyContent="space-around">
          {menuItems.slice(0, 4).map((item) => {
            return (
              <PermissionGuard key={item.key} permission={item.permission}>
                <MobileMenuButton
                  key={item.key}
                  labelId={item.labelId}
                  Icon={item.Icon}
                  selected={
                    selectedPathname.indexOf(item.pathname) !== -1 ||
                    item.subPathnames?.some((p) => selectedPathname.includes(p))
                  }
                  item={item}
                  onClick={handleMenuClick}
                  badgeCount={item.key === "chatwhatsapp" ? unansweredTotal : 0}
                />
              </PermissionGuard>
            );
          })}
          {/* More button - always show as 5th item */}
          <MobileMenuButton key="more" labelId="More" Icon={MoreHorizontalIcon} onClick={handleClickMoreMenu} />
        </MobileMenu>

        {showMenu && (
          <Box
            position="fixed"
            bottom={50}
            left={0}
            width="100%"
            display="flex"
            justifyContent="space-around"
            flexWrap="wrap"
            className={classes.moreMenuMobile}
          >
            {menuItems.slice(4).filter((item) => item.key !== "more").map((item) => {
              return (
                <PermissionGuard key={item.key} permission={item.permission}>
                  <MobileMenuButton
                    key={item.key}
                    labelId={item.labelId}
                    Icon={item.Icon}
                    selected={
                      selectedPathname.indexOf(item.pathname) !== -1 ||
                      item.subPathnames?.some((p) => selectedPathname.includes(p))
                    }
                    menuItems={menuItems}
                    item={item}
                    onClick={handleMenuClick}
                    style={item.style}
                  />
                </PermissionGuard>
              );
            })}
            <Button onClick={handleCloseButton}>{t("Close")}</Button>
          </Box>
        )}
      </Hidden>
    </>
  );
}

export default observer(Nav);
